/**
 * Single source of truth for Sentry tracking config in sessionStorage.
 * All reads/writes of sentry_tracking_* and the config shape live here.
 */

// Storage keys (used by readers, writers, and logout clear)
export const SENTRY_TRACKING_CONFIG_KEY = 'sentry_tracking_config';
export const SENTRY_TRACKING_LEVEL_KEY = 'sentry_tracking_level';
export const SENTRY_TRACKING_UPDATED_AT_KEY = 'sentry_tracking_updated_at';
export const SENTRY_SESSION_ID_KEY = 'sentry_session_id';
export const SESSION_USER_ID_KEY = 'segment_session_user_id';

export function getOrCreateSessionId(): string {
  try {
    let id = sessionStorage.getItem(SENTRY_SESSION_ID_KEY);
    if (!id) {
      id = `session_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
      sessionStorage.setItem(SENTRY_SESSION_ID_KEY, id);
    }
    return id;
  } catch {
    return `session_${Date.now()}`;
  }
}

export function setSessionUserId(userId: string): void {
  try {
    sessionStorage.setItem(SESSION_USER_ID_KEY, userId);
  } catch {
    // ignore
  }
}

export function getAndClearSessionUserId(): string | null {
  try {
    const id = sessionStorage.getItem(SESSION_USER_ID_KEY);
    sessionStorage.removeItem(SESSION_USER_ID_KEY);
    return id;
  } catch {
    return null;
  }
}

export interface SentryTrackingConfig {
  isTracked: boolean;
  trackingLevel: string | null;
  trackingReason: string | null;
  entityType?: string | null;
  entityId?: string | null;
  entityName?: string | null;
  entityEmail?: string | null;
  userId?: string | null;
  accountId?: string | null;
  accountName?: string | null;
  schoolId?: string | null;
  schoolName?: string | null;
  updatedAt: number;
}

export interface SentryTrackingUserContext {
  userId?: string;
  accountId?: string;
  accountName?: string;
  schoolId?: string;
  schoolName?: string;
}

/**
 * Single reader: parse config from sessionStorage with legacy fallback.
 * Normalizes updatedAt to number. Removes invalid JSON from storage.
 */
export function getSentryTrackingConfig(): SentryTrackingConfig | null {
  try {
    const raw = sessionStorage.getItem(SENTRY_TRACKING_CONFIG_KEY);
    if (raw) {
      try {
        const parsed = JSON.parse(raw) as Record<string, unknown>;
        const updatedAt =
          typeof parsed?.updatedAt === 'number' && Number.isFinite(parsed.updatedAt)
            ? parsed.updatedAt
            : Date.now();
        const str = (v: unknown): string | null =>
          typeof v === 'string' ? v : null;
        return {
          isTracked: Boolean(parsed?.isTracked),
          trackingLevel: str(parsed?.trackingLevel),
          trackingReason: str(parsed?.trackingReason),
          entityType: str(parsed?.entityType),
          entityId: str(parsed?.entityId),
          entityName: str(parsed?.entityName),
          entityEmail: str(parsed?.entityEmail),
          userId: str(parsed?.userId),
          accountId: str(parsed?.accountId),
          accountName: str(parsed?.accountName),
          schoolId: str(parsed?.schoolId),
          schoolName: str(parsed?.schoolName),
          updatedAt,
        };
      } catch {
        sessionStorage.removeItem(SENTRY_TRACKING_CONFIG_KEY);
        return null;
      }
    }

    const level = sessionStorage.getItem(SENTRY_TRACKING_LEVEL_KEY);
    if (level) {
      const rawUpdatedAt = sessionStorage.getItem(SENTRY_TRACKING_UPDATED_AT_KEY);
      const updatedAt =
        rawUpdatedAt != null ? Number(rawUpdatedAt) : Date.now();
      return {
        isTracked: true,
        trackingLevel: level,
        trackingReason: 'stored',
        updatedAt: Number.isFinite(updatedAt) ? updatedAt : Date.now(),
      };
    }

    return null;
  } catch {
    return null;
  }
}

/**
 * User context derived from stored config (for tags/context in Sentry).
 */
export function getSentryTrackingUserContext(): SentryTrackingUserContext {
  const config = getSentryTrackingConfig();
  if (!config) return {};
  return {
    userId: config.userId ?? undefined,
    accountId: config.accountId ?? undefined,
    accountName: config.accountName ?? undefined,
    schoolId: config.schoolId ?? undefined,
    schoolName: config.schoolName ?? undefined,
  };
}

/**
 * Tracking level only (for sampling decisions). Prefer getSentryTrackingConfig() when you need full config.
 */
export function getSentryTrackingLevel(): string | null {
  const config = getSentryTrackingConfig();
  if (config?.trackingLevel) return config.trackingLevel;
  return sessionStorage.getItem(SENTRY_TRACKING_LEVEL_KEY);
}

/**
 * Single writer: store config in sessionStorage and legacy keys.
 */
export function storeSentryTrackingConfig(
  isTracked: boolean,
  level: string | null,
  reason: string | null,
  entityType?: string | null,
  entityId?: string | null,
  entityName?: string | null,
  userEmail?: string | null,
  userId?: string | null,
  accountId?: string | null,
  accountName?: string | null,
  schoolId?: string | null,
  schoolName?: string | null
): void {
  try {
    const trackingConfig: SentryTrackingConfig = {
      isTracked,
      trackingLevel: level,
      trackingReason: reason,
      entityType: entityType ?? null,
      entityId: entityId ?? null,
      entityName: entityName ?? null,
      entityEmail: userEmail ?? null,
      userId: userId ?? entityId ?? null,
      accountId: accountId ?? null,
      accountName: accountName ?? null,
      schoolId: schoolId ?? null,
      schoolName: schoolName ?? null,
      updatedAt: Date.now(),
    };
    sessionStorage.setItem(SENTRY_TRACKING_CONFIG_KEY, JSON.stringify(trackingConfig));
    if (isTracked && level) {
      sessionStorage.setItem(SENTRY_TRACKING_LEVEL_KEY, level);
      sessionStorage.setItem(SENTRY_TRACKING_UPDATED_AT_KEY, String(trackingConfig.updatedAt));
    } else {
      sessionStorage.removeItem(SENTRY_TRACKING_LEVEL_KEY);
      sessionStorage.removeItem(SENTRY_TRACKING_UPDATED_AT_KEY);
    }
  } catch {
    // ignore
  }
}
